// Shared formatting. Kept in one place so a dollar figure or a date reads
// identically on the Home tab and on a customer's detail page.

export const formatCents = (cents: number, withCents = false): string =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: withCents ? 2 : 0,
    maximumFractionDigits: withCents ? 2 : 0,
  });

export const formatNumber = (value: number): string => value.toLocaleString("en-US");

/**
 * "1.2M" / "382k" / "940" — a compact axis tick.
 *
 * Exists because a Y axis is a fixed-width gutter and a full-precision label
 * silently overflows it: at 44px, `200000` rendered as `00000` on the message
 * volume charts, and `120000` as `20000` on the delivery chart — not truncated
 * visibly, but *reading as a different, smaller, entirely plausible number*.
 * That is the worst kind of chart bug, so axes abbreviate rather than widen
 * indefinitely. Exact values stay reachable in the tooltip and the Table view.
 */
export const formatCompactNumber = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000) {
    const millions = value / 1_000_000;
    return `${millions % 1 === 0 ? millions : millions.toFixed(1)}M`;
  }
  if (abs >= 1_000) {
    const thousands = value / 1_000;
    return `${thousands % 1 === 0 ? thousands : thousands.toFixed(1)}k`;
  }
  return String(value);
};

export const formatPercent = (fraction: number | null, digits = 1): string =>
  fraction === null ? "—" : `${(fraction * 100).toFixed(digits)}%`;

/**
 * "$8k" / "$1.2M" / "$450" — a compact CURRENCY axis tick, from CENTS.
 *
 * Every money figure this dashboard stores is in cents, and a chart handed
 * those integers raw draws an axis of bare numbers a hundred times too large:
 * the customer revenue chart's "800000" meant $8,000. Charts that plot money
 * must pass this so the axis says what the bars mean.
 */
export const formatCentsCompact = (cents: number): string =>
  `$${formatCompactNumber(Math.round(cents / 100))}`;

/** "Sep 6, 2026" — the absolute form, used in title attributes and fact lists. */
export const formatDate = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

/**
 * "Jan 2, 2022" — the absolute form for a bare 'YYYY-MM-DD' DAY KEY.
 *
 * Distinct from `formatDate` because the input is different in kind.
 * `formatDate` takes an instant (an ISO timestamp) and is right to render it in
 * the reader's own timezone. A day key is not an instant — it is a UTC day
 * label, the ID of an `adminPlatformStats` row or a `byDay` entry — and
 * rendering it locally shifts it a day west of UTC. That is not hypothetical:
 * the volume history's `windowFrom` of 2022-01-02 rendered as "Jan 1, 2022"
 * through `formatDate`, claiming a day of coverage that does not exist.
 */
export const formatUtcDayKey = (day: string): string => {
  if (!day) return "—";
  const date = new Date(`${day}T00:00:00.000Z`);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
};

/** "Sep 6" — compact axis tick for a day-series chart. */
export const formatDayTick = (iso: string): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
};

/**
 * "3 months ago" / "in 12 days". Table cells show this with the absolute date
 * in a `title` attribute — relative reads faster when scanning, absolute is
 * what you need when you actually care.
 */
export const formatRelative = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";

  const deltaMs = date.getTime() - Date.now();
  const deltaDays = Math.round(deltaMs / 86400000);
  const absDays = Math.abs(deltaDays);

  const formatter = new Intl.RelativeTimeFormat("en-US", { numeric: "auto" });
  if (absDays < 1) return "today";
  if (absDays < 30) return formatter.format(deltaDays, "day");
  if (absDays < 365) return formatter.format(Math.round(deltaDays / 30), "month");
  return formatter.format(Math.round(deltaDays / 365), "year");
};

/**
 * "5m ago" / "3h ago" / "2d ago", then the absolute date past a week.
 *
 * `formatRelative` above is day-granular ("today"), which is too coarse for a
 * DM thread where the question is whether something happened a minute ago or
 * this morning.
 */
export const formatAgo = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";

  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
};

/** "Oct 24, 3:41 PM" — the absolute form for a message timestamp. */
export const formatDateTime = (iso: string): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

export const initialsFor = (fullName: string, username: string): string => {
  const source = (fullName || username || "").trim();
  if (!source) return "?";
  const parts = source.split(/\s+/).slice(0, 2);
  return parts.map(part => part[0]?.toUpperCase() ?? "").join("") || "?";
};
